import { PlusSignIcon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { useAuthContext } from "providers/Auth";
import { LeadTag } from "api/lead-tags";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "shared/components/Popover";
import { cx } from "shared/utils";
import { LeadTagPill } from "shared/app/LeadTags/components/LeadTagPill";
import { LeadTagsCompactList } from "shared/app/LeadTags/components/LeadTagsCompactList";
import { LeadTagPickerContent } from "shared/app/LeadTags/components/LeadTagPickerContent";

const EMPTY_TAGS: LeadTag[] = [];

// "+ Add tags" shows on hover of the field itself, or of a Pipeline card (group/card).
const EmptyTagsPrompt = ({ emptyText }: { emptyText?: string }) => (
  <>
    {emptyText ? (
      <span className="text-sm text-content-1000 group-hover:hidden group-data-[state=open]:hidden">
        {emptyText}
      </span>
    ) : null}
    <span className="hidden items-center gap-1 text-xs font-bold text-primary-500 group-hover:inline-flex group-hover/card:inline-flex group-data-[state=open]:inline-flex">
      <HugeiconsIcon icon={PlusSignIcon} size={14} strokeWidth={2.4} />
      Add tags
    </span>
  </>
);

const TriggerContent = ({
  tags,
  layout,
  emptyText
}: {
  tags: LeadTag[];
  layout: "compact" | "full";
  emptyText?: string;
}) => {
  if (tags.length === 0) return <EmptyTagsPrompt emptyText={emptyText} />;
  if (layout === "compact") return <LeadTagsCompactList tags={tags} />;
  return (
    <>
      {tags.map((tag) => (
        <LeadTagPill key={tag.id} name={tag.name} />
      ))}
    </>
  );
};

/**
 * A Lead's tags with the picker behind them. `compact` shows two tags A to Z plus "+N"
 * (Leads table, Pipeline card); `full` wraps every tag (Lead page). Hovering lists all
 * tags; clicking opens the picker without opening the Lead.
 */
export const LeadTagsPicker = ({
  leadId,
  tags = EMPTY_TAGS,
  layout = "compact",
  emptyText,
  className
}: {
  leadId: number;
  tags?: LeadTag[];
  layout?: "compact" | "full";
  emptyText?: string;
  className?: string;
}) => {
  const { type } = useAuthContext();

  return (
    <Popover placement="bottom-start">
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Edit tags"
          title={tags.map((tag) => tag.name).join(", ") || undefined}
          className={cx(
            "group flex w-full min-w-0 items-center gap-1.5 rounded-lg border border-transparent px-2 py-1 text-left",
            "hover:border-alpha-100 hover:bg-alpha-20 data-[state=open]:border-alpha-100 data-[state=open]:bg-alpha-20",
            layout === "full" ? "flex-wrap" : "overflow-hidden",
            className
          )}
          onClick={(event) => event.stopPropagation()}
        >
          <TriggerContent tags={tags} layout={layout} emptyText={emptyText} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        initialFocus={-1}
        className="max-w-[300px] w-full p-0 rounded-lg border border-alpha-100 shadow-lg bg-content-1000-inverted z-50 overflow-hidden"
      >
        <LeadTagPickerContent
          leadId={leadId}
          tags={tags}
          canCreate={type.adminPrivileges}
        />
      </PopoverContent>
    </Popover>
  );
};
